import React from 'react';
import { Typography, Button, IconButton, Box, darken } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useTranslation } from 'react-i18next';
import { renderFrameSVG, courtDimensions } from './Functions_Board_Grapchics.tsx';

interface PropsPhasesSectionPlayBuilder {
    title: string;
    frames: any[];
    currentFrameIndex: number;
    courtType: 'halfcourt' | 'fullcourt';
    handleSelectFrame: (index: number) => void;
    handleDeleteFrame: (index: number) => void;
    handleCreateNewFrame: () => void;
    handleDuplicateFrame: (index: number) => void;
    handleReorderFrames: (reorderedFrames: any[]) => void;
    portrait?: boolean;
    use3DPlayers?: boolean;
}

export default function PhasesSectionPlayBuilder({ title, frames, currentFrameIndex, courtType, handleSelectFrame, handleDeleteFrame, handleCreateNewFrame, handleDuplicateFrame, handleReorderFrames, portrait = false, use3DPlayers = true }: PropsPhasesSectionPlayBuilder) {
    const { t } = useTranslation();

    // Calculate thumbnail dimensions
    const thumbnailWidth = 200;
    const thumbnailHeight = thumbnailWidth * (courtDimensions[courtType].height / courtDimensions[courtType].width);

    const moveFrameUp = (index: number) => {
        if (index > 0) {
            const newFrames = [...frames];
            [newFrames[index], newFrames[index - 1]] = [newFrames[index - 1], newFrames[index]];
            handleReorderFrames(newFrames);
        }
    };

    const moveFrameDown = (index: number) => {
        if (index < frames.length - 1) {
            const newFrames = [...frames];
            [newFrames[index], newFrames[index + 1]] = [newFrames[index + 1], newFrames[index]];
            handleReorderFrames(newFrames);
        }
    };

    return (
        <div>
            {!portrait ? (
                <>
                    <Typography variant="h6" style={{ fontWeight: 'bold', textAlign: 'center', color: 'black', marginBottom: '10px' }}>
                        {t('DialogPlayBuilder01')}
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {frames.map((frame, index) => (
                            <div
                                key={frame.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: '8px',
                                }}
                            >
                                {/* Frame container with hover delete button */}
                                <div
                                    style={{
                                        position: 'relative',
                                        width: thumbnailWidth,
                                        height: thumbnailHeight,
                                        border: index === currentFrameIndex ? '2px solid #00398f' : '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        backgroundColor: index === currentFrameIndex ? '#f8f9ff' : 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease-in-out',
                                        overflow: 'hidden',
                                    }}
                                    onClick={() => handleSelectFrame(index)}
                                    onMouseEnter={(e) => {
                                        const deleteBtn = e.currentTarget.querySelector('[data-delete-btn]') as HTMLElement;
                                        const phaseNumber = e.currentTarget.querySelector('[data-phase-number]') as HTMLElement;
                                        const upBtn = e.currentTarget.querySelector('[data-up-btn]') as HTMLElement;
                                        const downBtn = e.currentTarget.querySelector('[data-down-btn]') as HTMLElement;
                                        if (deleteBtn) deleteBtn.style.opacity = '1';
                                        if (phaseNumber) phaseNumber.style.opacity = '1';
                                        if (upBtn) upBtn.style.opacity = '1';
                                        if (downBtn) downBtn.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        const deleteBtn = e.currentTarget.querySelector('[data-delete-btn]') as HTMLElement;
                                        const phaseNumber = e.currentTarget.querySelector('[data-phase-number]') as HTMLElement;
                                        const upBtn = e.currentTarget.querySelector('[data-up-btn]') as HTMLElement;
                                        const downBtn = e.currentTarget.querySelector('[data-down-btn]') as HTMLElement;
                                        if (deleteBtn) deleteBtn.style.opacity = '0';
                                        if (phaseNumber) phaseNumber.style.opacity = '0';
                                        if (upBtn) upBtn.style.opacity = '0';
                                        if (downBtn) downBtn.style.opacity = '0';
                                    }}
                                >
                                    <svg
                                        width="100%"
                                        height="100%"
                                        viewBox={`0 0 ${courtDimensions[courtType].width} ${courtDimensions[courtType].height}`}
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        {renderFrameSVG(
                                            frame,
                                            courtType,
                                            undefined, // playNid
                                            {
                                                courtRenderMode: 'woodBackground',
                                                use3DPlayers: false,
                                            }
                                        ).props.children}
                                    </svg>

                                    {/* Duplicate Phase button - bottom left */}
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDuplicateFrame(index);
                                        }}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            bottom: '4px',
                                            left: '4px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                            color: '#666',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            fontSize: '9px',
                                            padding: '2px 6px',
                                            minWidth: 'auto',
                                            height: '20px',
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                color: '#1a252f',
                                                transform: 'scale(1.05)',
                                            },
                                        }}
                                    >
                                        {t('DialogPlayBuilder03')}
                                    </Button>

                                    {/* Phase number on hover */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '4px',
                                            left: '4px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            color: '#2c3e50',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease-in-out',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            whiteSpace: 'nowrap',
                                        }}
                                        data-phase-number
                                    >
                                        {t('PhasesSectionPlayBuilder00')} {index + 1}
                                    </Box>

                                    {/* Delete button on hover */}
                                    <IconButton
                                        data-delete-btn
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFrame(index);
                                        }}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            color: '#b0bec5',
                                            width: '24px',
                                            height: '24px',
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                                color: darken('#b0bec5', 0.3),
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '16px',
                                            }
                                        }}
                                    >
                                        <DeleteOutlineRoundedIcon />
                                    </IconButton>

                                    {/* Move Up button - top right */}
                                    <Button
                                        data-up-btn
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveFrameUp(index);
                                        }}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            right: '4px',
                                            transform: 'translateY(-50%) translateY(-12px)',
                                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                            color: index === 0 ? '#b0bec5' : '#666',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            fontSize: '9px',
                                            padding: '2px 6px',
                                            minWidth: 'auto',
                                            width: '20px',
                                            height: '20px',
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            opacity: 0,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                color: index === 0 ? '#b0bec5' : '#1a252f',
                                                transform: 'translateY(-50%) translateY(-12px) scale(1.05)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '12px',
                                            }
                                        }}
                                        disabled={index === 0}
                                    >
                                        <KeyboardArrowUpRoundedIcon />
                                    </Button>

                                    {/* Move Down button - bottom right */}
                                    <Button
                                        data-down-btn
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveFrameDown(index);
                                        }}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            right: '4px',
                                            transform: 'translateY(-50%) translateY(12px)',
                                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                            color: index === frames.length - 1 ? '#b0bec5' : '#666',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            fontSize: '9px',
                                            padding: '2px 6px',
                                            minWidth: 'auto',
                                            width: '20px',
                                            height: '20px',
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            opacity: 0,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                color: index === frames.length - 1 ? '#b0bec5' : '#1a252f',
                                                transform: 'translateY(-50%) translateY(12px) scale(1.05)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '12px',
                                            }
                                        }}
                                        disabled={index === frames.length - 1}
                                    >
                                        <KeyboardArrowDownRoundedIcon />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCreateNewFrame}
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            {t('DialogPlayBuilder02')}
                        </Button>
                    </Box>
                </>
            ) : (
                <>
                    <Typography style={{ fontWeight: 'bold', textAlign: 'center', paddingTop: '0px', marginBottom: 0, color: '#00398f' }}>
                        {title}
                    </Typography>
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            maxWidth: '600px',
                            margin: 'auto',
                            paddingBottom: '0px',
                        }}
                    >
                        {/* Grupo de Navegación: Arrow Back, Phase, Arrow Forward */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                onClick={() => handleSelectFrame(currentFrameIndex - 1)}
                                size="small"
                                style={{
                                    color: currentFrameIndex === 0 ? '#b0bec5' : '#00398f',
                                    width: '40px',
                                    height: '40px',
                                    padding: '8px',
                                }}
                                disabled={currentFrameIndex === 0}
                            >
                                <KeyboardArrowLeftRoundedIcon />
                            </IconButton>

                            <Typography
                                variant="body1"
                                style={{
                                    fontWeight: 'bold',
                                    margin: '0 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'black'
                                }}
                            >
                                {t('PlayCard00')} {frames[currentFrameIndex].id}
                            </Typography>

                            <IconButton
                                onClick={() => handleSelectFrame(currentFrameIndex + 1)}
                                size="small"
                                style={{
                                    color: currentFrameIndex === frames.length - 1 ? '#b0bec5' : '#00398f',
                                    width: '40px',
                                    height: '40px',
                                    padding: '8px',
                                }}
                                disabled={currentFrameIndex === frames.length - 1}
                            >
                                <KeyboardArrowRightRoundedIcon />
                            </IconButton>
                        </div>

                        {/* Botón "Next Phase" */}
                        <Button
                            onClick={handleCreateNewFrame}
                            size="small"
                            variant="outlined"
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 1.5,
                                textTransform: 'none',
                                // color: '#',
                                borderColor: '#00398f',
                                '&:hover': {
                                    // backgroundColor: '#f4f4f4',
                                    // color: '#00398f',
                                    // borderColor: '#00398f',
                                },
                            }}
                        >
                            {t('DialogPlayBuilder02')}
                        </Button>

                        {/* Duplicate Phase button */}
                        {!portrait && (
                            <Button
                                onClick={() => handleDuplicateFrame(currentFrameIndex)}
                                size="small"
                                variant="outlined"
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#2c3e50',
                                    borderColor: '#2c3e50',
                                    fontSize: '11px',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                    height: '32px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: '#f8f9fa',
                                        borderColor: '#1a252f',
                                        color: '#1a252f',
                                    },
                                }}
                            >
                                {t('DialogPlayBuilder03') || 'Duplicate'}
                            </Button>
                        )}

                        {/* Delete frame button */}
                        <IconButton
                            onClick={() => handleDeleteFrame(currentFrameIndex)}
                            size="small"
                            sx={{
                                color: '#b0bec5',
                                width: '40px',
                                height: '40px',
                                padding: '8px',
                                marginLeft: 'auto',
                                '&:hover': {
                                    color: darken('#b0bec5', 0.3),
                                },
                            }}
                        >
                            <DeleteOutlineRoundedIcon />
                        </IconButton>
                    </Box>
                </>
            )}
        </div>
    );
}
